/**
 * Read-only client for the publish-flow `website-refresh-keys` endpoint:
 *
 *   GET /publish-flow/{jazelId}/{correlationId}/website-refresh-keys
 *       ?pageSize=&cursor=&search=
 *
 * The refreshed cache-key LIST is persisted server-side as a JSONL artifact;
 * this service pages and searches it so the dialog never needs the full set up
 * front and can find a VIN/key that is not yet loaded. Mirrors the
 * request/auth conventions of `publishFlowDecisionService`.
 */

export type WebsiteRefreshKeyType = 'vehicle' | 'account' | 'other'

export interface WebsiteRefreshKeyRecord {
  key: string
  type: WebsiteRefreshKeyType
  vin?: string
  accountId?: string
}

export interface WebsiteRefreshKeysPage {
  records: WebsiteRefreshKeyRecord[]
  totalRecords: number
  nextCursor?: string
  search?: string
}

export type WebsiteRefreshKeysReadResult =
  | { status: 'ok'; data: WebsiteRefreshKeysPage }
  | { status: 'unavailable'; reason: 'network' | 'http5xx' | 'route' | 'request'; message: string }

const trimBase = (endpoint: string) => endpoint.replace(/\/+$/g, '')

const authHeaders = (sessionToken: string) => ({
  Accept: 'application/json',
  Authorization: `Bearer ${sessionToken}`,
})

function websiteRefreshKeysPath(jazelId: string, correlationId: string): string {
  return `/publish-flow/${encodeURIComponent(jazelId)}/${encodeURIComponent(
    correlationId,
  )}/website-refresh-keys`
}

function buildQuery(input: { pageSize?: number; cursor?: string; search?: string }): URLSearchParams {
  const query = new URLSearchParams()
  if (typeof input.pageSize === 'number' && Number.isFinite(input.pageSize)) {
    query.set('pageSize', String(input.pageSize))
  }
  if (typeof input.cursor === 'string' && input.cursor.length > 0) {
    query.set('cursor', input.cursor)
  }
  if (typeof input.search === 'string') {
    const trimmed = input.search.trim()
    if (trimmed.length > 0) query.set('search', trimmed)
  }
  return query
}

function normalizeRecord(raw: unknown): WebsiteRefreshKeyRecord | null {
  if (!raw || typeof raw !== 'object') return null
  const record = raw as Record<string, unknown>
  const key = typeof record.key === 'string' ? record.key : ''
  if (key.length === 0) return null
  const type: WebsiteRefreshKeyType =
    record.type === 'vehicle' || record.type === 'account' ? record.type : 'other'
  const out: WebsiteRefreshKeyRecord = { key, type }
  if (typeof record.vin === 'string' && record.vin.length > 0) out.vin = record.vin
  if (typeof record.accountId === 'string' && record.accountId.length > 0) {
    out.accountId = record.accountId
  }
  return out
}

function parsePage(data: unknown): WebsiteRefreshKeysPage {
  const obj = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>
  const records = Array.isArray(obj.records)
    ? obj.records.map(normalizeRecord).filter((r): r is WebsiteRefreshKeyRecord => r !== null)
    : []
  const totalRecords =
    typeof obj.totalRecords === 'number' && Number.isFinite(obj.totalRecords)
      ? obj.totalRecords
      : records.length
  const page: WebsiteRefreshKeysPage = { records, totalRecords }
  if (typeof obj.nextCursor === 'string' && obj.nextCursor.length > 0) {
    page.nextCursor = obj.nextCursor
  }
  if (typeof obj.search === 'string' && obj.search.length > 0) {
    page.search = obj.search
  }
  return page
}

/**
 * Fetch one page of refreshed cache keys. Never throws on transport/HTTP
 * issues: a non-2xx or network failure surfaces as `unavailable` so the dialog
 * can show a graceful state. The backend returns an empty 200 page for runs
 * with no captured keys, which maps to `ok` with `totalRecords: 0`.
 */
export async function tryGetWebsiteRefreshKeys(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
  pageSize?: number
  cursor?: string
  search?: string
}): Promise<WebsiteRefreshKeysReadResult> {
  const base = trimBase(params.endpoint)
  const path = websiteRefreshKeysPath(params.jazelId, params.correlationId)
  const qs = buildQuery({
    pageSize: params.pageSize,
    cursor: params.cursor,
    search: params.search,
  }).toString()
  const url = `${base}${path}${qs.length > 0 ? `?${qs}` : ''}`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: authHeaders(params.sessionToken),
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network error fetching refreshed cache keys.',
    }
  }

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const message =
      (payload && typeof payload === 'object' && typeof (payload as { message?: unknown }).message === 'string'
        ? (payload as { message: string }).message
        : '') || `Refreshed cache keys unavailable (${response.status}).`
    if (response.status >= 500) return { status: 'unavailable', reason: 'http5xx', message }
    if (response.status === 404) return { status: 'unavailable', reason: 'route', message }
    return { status: 'unavailable', reason: 'request', message }
  }

  const data =
    payload && typeof payload === 'object' && 'data' in payload
      ? (payload as { data: unknown }).data
      : payload
  return { status: 'ok', data: parsePage(data) }
}
